/**
 * AuthenticatedImage Component
 * Fetches images from authenticated endpoints and displays them
 * This solves the problem of <img> tags not sending auth headers
 */

import { useState, useEffect, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';

const AuthenticatedImage = ({
  src,
  alt = 'Image',
  style = {},
  onError,
  onLoad,
  fallback = null,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Use refs for callbacks to avoid re-fetching when inline callbacks change
  const onErrorRef = useRef(onError);
  const onLoadRef = useRef(onLoad);
  useEffect(() => {
    onErrorRef.current = onError;
    onLoadRef.current = onLoad;
  });

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    const fetchImage = async () => {
      if (!src) {
        setLoading(false);
        setError(true);
        return;
      }

      try {
        setLoading(true);
        setError(false);

        const authToken = localStorage.getItem('item');
        const currentBranch = localStorage.getItem('currentBranch');

        const headers = {};
        if (authToken) {
          headers['Authorization'] = authToken;
        }
        if (currentBranch) {
          try {
            const branchData = JSON.parse(currentBranch);
            if (branchData?.id) {
              headers['X-Current-Branch'] = branchData.id;
            }
          } catch (e) {
            // Invalid JSON in currentBranch
          }
        }

        const response = await fetch(src, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const blob = await response.blob();

        if (isMounted) {
          objectUrl = URL.createObjectURL(blob);
          setImageSrc(objectUrl);
          setLoading(false);
          if (onLoadRef.current) onLoadRef.current();
        }
      } catch (err) {
        // Only log if component is still mounted
        if (isMounted) {
          console.error('Error fetching authenticated image:', err);
          setError(true);
          setLoading(false);
          if (onErrorRef.current) onErrorRef.current(err);
        }
      }
    };

    fetchImage();

    // Cleanup: revoke object URL when component unmounts or src changes
    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]); // Only re-fetch when src changes

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        width={style.width || '100%'}
        height={style.height || '100%'}
        sx={{ bgcolor: 'grey.800' }}
      />
    );
  }

  if (error) {
    if (fallback) {
      return fallback;
    }
    return (
      <Box
        sx={{
          width: style.width || '100%',
          height: style.height || '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.900',
          color: 'grey.500',
          flexDirection: 'column',
          ...style,
        }}
        {...props}
      >
        <ImageIcon sx={{ fontSize: 40 }} />
      </Box>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      style={style}
      {...props}
    />
  );
};

export default AuthenticatedImage;
